import { LogIn } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CardCover,
  CardGrid,
  ConversationLayout,
  DocumentParty,
  MiniChart,
  PrintDocument,
  SettingRow,
  SettingsSection,
  StepsLayout,
  Switch,
  WeekAgenda,
} from '../../components/patterns/index.ts';
import {
  Badge,
  Button,
  Field,
  Input,
  NumCell,
  PageHeader,
  Panel,
  Table,
  TotalRow,
  ValueTable,
} from '../../components/ui.tsx';
import { BADGE_TONE, type SampleDomain } from '../samples/index.ts';

// As outras telas da bancada do estúdio (ADR 0133, parte 3): Agenda, Galeria, Cadastro em
// etapas, Conversas, Relatório, Configurações, Documento impresso e Entrar.

export function AgendaScreen({ d }: { d: SampleDomain }) {
  const { i18n } = useTranslation();
  const monday = Date.UTC(2026, 10, 2);
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={d.agenda.title} />
      <WeekAgenda
        label={d.agenda.title}
        today={1}
        from={d.agenda.start * 60}
        to={(d.agenda.start + 10) * 60}
        days={[0, 1, 2, 3, 4].map((i) =>
          new Date(monday + i * 864e5).toLocaleDateString(i18n.language, {
            weekday: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          }),
        )}
        events={d.agenda.events.map(([day, start, len, what], i) => ({
          id: String(i),
          day,
          start: start * 60,
          end: (start + len) * 60,
          what,
        }))}
        renderEvent={(e) => <span className="block truncate font-medium">{e.what}</span>}
      />
    </div>
  );
}

export function GalleryScreen({ d }: { d: SampleDomain }) {
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={d.gallery.title} description={d.gallery.sub} />
      <CardGrid
        items={d.gallery.items}
        keyOf={(g) => g[0]}
        renderCard={([name, sub, price, st]) => (
          <>
            <CardCover name={name} />
            <div className="flex flex-col gap-1 p-3 text-sm">
              <span className="font-medium">{name}</span>
              <span className="text-xs text-muted">{sub}</span>
              <span className="flex items-center justify-between gap-2 pt-1">
                <span className="tabular-nums">{price}</span>
                <Badge tone={BADGE_TONE[st[1]]}>{st[0]}</Badge>
              </span>
            </div>
          </>
        )}
      />
    </div>
  );
}

export function WizardScreen({ d }: { d: SampleDomain }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={d.wizard.title} />
      <StepsLayout
        steps={d.wizard.steps}
        current={step}
        onBack={() => setStep((s) => Math.max(0, s - 1))}
        onNext={() => setStep((s) => (s + 1) % d.wizard.steps.length)}
        finishLabel={t('style.demo.save')}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {d.wizard.fields.map(([label, value]) => (
            <Field key={label} label={label}>
              <Input defaultValue={value} />
            </Field>
          ))}
        </div>
      </StepsLayout>
    </div>
  );
}

export function ConversationScreen({ d }: { d: SampleDomain }) {
  const [open, setOpen] = useState<string | null>(d.chat.threads[0]![0]);
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={d.chat.title} />
      <ConversationLayout
        threads={d.chat.threads.map(([title, preview, , unread]) => ({
          id: title,
          title,
          preview,
          unread,
        }))}
        openId={open}
        onOpen={setOpen}
        header={
          <span className="flex flex-col">
            <span className="text-sm font-medium">{d.chat.with[0]}</span>
            <span className="text-xs text-muted">{d.chat.with[1]}</span>
          </span>
        }
        messages={d.chat.msgs.map(([who, text, when], i) => ({
          id: String(i),
          author: who === 'me' ? d.user[0] : d.chat.with[0],
          when,
          text,
          mine: who === 'me',
        }))}
        onSend={() => {}}
      />
    </div>
  );
}

export function ReportScreen({ d }: { d: SampleDomain }) {
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={d.report.title} description={d.report.sub} />
      <Panel>
        <MiniChart
          label={d.report.title}
          points={d.report.rows.map((r) => ({
            label: r[0]!.split(' ')[0]!,
            value: Number(r[r.length - 1]!.replace(/\./g, '').replace(',', '.')),
          }))}
        />
      </Panel>
      <ValueTable
        label={d.report.title}
        head={d.report.cols.map((c, i) => (i ? { label: c, align: 'right' as const } : c))}
      >
        {d.report.rows.map((r) => (
          <tr key={r[0]}>
            {r.map((c, i) => (i ? <NumCell key={i}>{c}</NumCell> : <td key={i}>{c}</td>))}
          </tr>
        ))}
        <TotalRow>
          {d.report.foot.map((c, i) => (i ? <NumCell key={i}>{c}</NumCell> : <td key={i}>{c}</td>))}
        </TotalRow>
      </ValueTable>
    </div>
  );
}

export function SettingsScreen({ d }: { d: SampleDomain }) {
  const { t } = useTranslation();
  const [on, setOn] = useState([true, false, true]);
  const rows = [
    [t('style.demo.cfgNotify'), t('style.demo.cfgNotifyHint')],
    [t('style.demo.cfgWeekly'), t('style.demo.cfgWeeklyHint')],
    [t('style.demo.cfgApprove'), t('style.demo.cfgApproveHint')],
  ];
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('style.demo.cfgTitle')} description={d.brand[0]} />
      <SettingsSection title={t('style.demo.cfgSection')} description={d.tagline}>
        {rows.map(([label, hint], i) => (
          <SettingRow
            key={label}
            label={label}
            description={hint}
            control={(id) => (
              <Switch
                checked={on[i]!}
                labelledBy={id}
                onChange={(v) => setOn((s) => s.map((x, j) => (j === i ? v : x)))}
              />
            )}
          />
        ))}
      </SettingsSection>
    </div>
  );
}

export function DocumentScreen({ d }: { d: SampleDomain }) {
  const { t } = useTranslation();
  const r = d.rows[d.mainIdx]!;
  return (
    <PrintDocument
      printable={false}
      title={d.list.title.split(' ')[0]!}
      code={r.id}
      issuedAt={r.d1}
      parties={
        <>
          <DocumentParty label={t('style.demo.docFrom')} lines={[d.brand[0], d.brand[1]]} />
          <DocumentParty label={t('style.demo.docTo')} lines={[r.title, r.c3]} />
        </>
      }
      footer={d.tagline}
    >
      <Table
        head={d.rec.sub.cols.map(([label, kind]) =>
          kind.startsWith('n') ? { label, align: 'right' as const } : label,
        )}
      >
        {d.rec.sub.rows.map((row) => (
          <tr key={row[0]}>
            {row.map((c, i) =>
              d.rec.sub.cols[i]?.[1].startsWith('n') ? (
                <NumCell key={i}>{c}</NumCell>
              ) : (
                <td key={i}>{c}</td>
              ),
            )}
          </tr>
        ))}
      </Table>
    </PrintDocument>
  );
}

export function SignInScreen({ d }: { d: SampleDomain }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[32rem] items-center justify-center">
      <div className="ui-panel flex w-full max-w-sm flex-col gap-4 rounded-lg bg-sheet p-6">
        <div className="flex items-center gap-3">
          {d.logo ? (
            <img src={d.logo} alt="" className="h-10 max-w-[8rem] object-contain" />
          ) : (
            <span className="flex size-10 items-center justify-center rounded-md bg-accent text-sm font-bold text-on-accent">
              {d.brand[2]}
            </span>
          )}
          <span className="flex flex-col">
            <span className="font-semibold">{d.brand[0]}</span>
            <span className="text-xs text-muted">{d.tagline}</span>
          </span>
        </div>
        <Field label={t('style.demo.email')}>
          <Input defaultValue={`${d.user[0].split(' ')[0]!.toLowerCase()}@exemplo.com`} />
        </Field>
        <Field label={t('style.demo.password')}>
          <Input type="password" defaultValue="exemplo-123" />
        </Field>
        <Button>
          <LogIn size={16} aria-hidden />
          {t('auth.signIn')}
        </Button>
      </div>
    </div>
  );
}
